import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Combobox } from "@/components/ui/Combobox";
import { DatePicker } from "@/components/ui/DatePicker";
import { Field } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { FindingList } from "@/components/qa/FindingList";
import { useKeyboard, type Shortcut } from "@/hooks/useKeyboard";
import { useReference } from "@/hooks/useReference";
import { useToast } from "@/hooks/useToast";
import { isolates as isolatesApi } from "@/lib/api";
import { db } from "@/lib/db";
import { toRegisterDate } from "@/lib/format";
import { inbound, type InboundPrefill } from "@/lib/phase2";
import { strings } from "@/lib/strings";
import type { IsolatePayload } from "@/lib/types";
import { AntibioticGrid } from "./AntibioticGrid";
import { ShortcutsOverlay } from "./ShortcutsOverlay";
import { useEntryForm, type SaveMode } from "./useEntryForm";

const CULTURE_OPTIONS = [
  { value: "growth", label: "Growth" },
  { value: "no_growth", label: "No growth" },
  { value: "no_significant_growth", label: "No significant growth" },
  { value: "mixed", label: "Mixed growth" },
];

const SEX_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "unknown", label: "Not recorded" },
];

const AGE_UNIT_OPTIONS = [
  { value: "years", label: "Years" },
  { value: "months", label: "Months" },
  { value: "days", label: "Days" },
];

const ISOLATE_NUMBER_OPTIONS = [
  { value: "1st", label: "1st" },
  { value: "2nd", label: "2nd" },
  { value: "3rd", label: "3rd" },
];

/**
 * The entry form (docs/08 §8.3) - the core screen of the platform.
 *
 * Three cards in register order (E-01), keyboard-first (E-14), usable down to
 * 360px (E-15), and fully usable offline.
 */
export function EntryForm({ initial }: { initial?: Partial<IsolatePayload> }) {
  const { organisms, antibiotics, wards, specimens, bundle } = useReference();
  const { notify } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  // A record from the laboratory system arrives as route state (IN-13). It is
  // a suggestion: it fills the form, and the volunteer confirms it.
  const inboundRecord = (location.state as { inbound?: InboundPrefill } | null)?.inbound;
  const seeded: Partial<IsolatePayload> | undefined =
    initial ?? (inboundRecord?.prefill as Partial<IsolatePayload> | undefined);

  const [showShortcuts, setShowShortcuts] = useState(false);
  const [dateText, setDateText] = useState(toRegisterDate(seeded?.specimen_date ?? null));
  const [dateError, setDateError] = useState<string | null>(null);

  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState<boolean>(false);

  const form = useEntryForm({
    organisms,
    specimens,
    qaRules: bundle?.qa_rules ?? [],
    initial: seeded,
    editId,
    expectedUpdatedAt,
  });

  useEffect(() => {
    if (!editId) return;
    const targetId = editId;
    let active = true;

    async function loadEdit() {
      try {
        let local = await db.isolates_local.get(targetId);
        if (!local) {
          local = await db.isolates_local.filter((r) => r.server_id === targetId).first();
        }

        if (local) {
          if (!active) return;
          form.setAllValues(local.payload);
          setDateText(toRegisterDate(local.payload.specimen_date ?? null));
          setEditingRecordId(local.record_id ?? null);
          setExpectedUpdatedAt(local.updated_at);
          setIsDraft(local.payload.status === "draft");
          return;
        }

        const remote = await isolatesApi.get(targetId);
        if (!active) return;
        form.setAllValues({
          lab_number: remote.lab_number,
          hospital_number: remote.hospital_number,
          specimen_date: remote.specimen_date,
          specimen_type: remote.specimen_type,
          ward_code: remote.ward_code,
          ward_raw: remote.ward_raw,
          age_value: remote.age_value,
          age_unit: remote.age_unit,
          sex: remote.sex,
          culture_result: remote.culture_result,
          organism_code: remote.organism_code,
          organism_as_written: remote.organism_as_written,
          isolate_number: remote.isolate_number,
          ast_results: remote.ast_results,
          status: remote.status,
          register_book: remote.register_book,
          register_page: remote.register_page,
          notes: remote.notes,
        });
        setDateText(toRegisterDate(remote.specimen_date ?? null));
        setEditingRecordId(remote.record_id);
        setExpectedUpdatedAt(remote.updated_at);
        setIsDraft(remote.status === "draft");
      } catch {
        if (active) {
          notify("Could not load the record to edit.", "error");
        }
      }
    }

    void loadEdit();
    return () => {
      active = false;
    };
  }, [editId]);

  const organismOptions = useMemo(
    () =>
      organisms.map((organism) => ({
        value: organism.code,
        label: organism.name,
        keywords: [organism.code, organism.whonet_code ?? ""],
      })),
    [organisms],
  );

  const wardOptions = useMemo(
    () => wards.map((ward) => ({ value: ward.code, label: ward.name, keywords: [ward.code] })),
    [wards],
  );

  const specimenOptions = useMemo(
    () => specimens.map((s) => ({ value: s.code, label: s.name, keywords: [s.code] })),
    [specimens],
  );

  const handleSave = async (mode: SaveMode) => {
    if (dateError) return;
    try {
      const outcome = await form.save(mode);
      if (!outcome.ok) {
        notify(strings.errors.validation, "error");
        return;
      }
      if (outcome.queued) {
        notify(strings.entry.savedOfflineToast, "success");
      } else if (outcome.recordId) {
        notify(strings.entry.savedToast(outcome.recordId), "success");
      }

      // Link the laboratory record only now that the isolate exists. Doing it
      // earlier would consume the suggestion even if the save had failed.
      if (inboundRecord && outcome.isolateId) {
        try {
          await inbound.link(inboundRecord.id, outcome.isolateId);
        } catch {
          notify(
            "Saved, but the laboratory record could not be marked as used. " +
              "It may be offered again.",
            "error",
          );
        }
      }
      if (editId) {
        if (mode === "next-organism") {
          setDateText("");
        } else {
          navigate(outcome.isolateId ? `/isolates/${outcome.isolateId}` : "/workbench");
        }
        return;
      }
      if (mode !== "next-organism") setDateText("");
    } catch {
      notify(strings.errors.generic, "error");
    }
  };

  const shortcuts: Shortcut[] = useMemo(
    () => [
      {
        key: "s",
        ctrl: true,
        allowInInput: true,
        description: "Save and new",
        handler: () => void handleSave("new"),
      },
      {
        key: "s",
        ctrl: true,
        shift: true,
        allowInInput: true,
        description: "Save and next organism",
        handler: () => void handleSave("next-organism"),
      },
      {
        key: "?",
        description: "Show shortcuts",
        handler: () => setShowShortcuts(true),
      },
    ],
    // handleSave closes over the current values, so it must be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.values, dateError],
  );

  useKeyboard(shortcuts);

  const fieldFindings = (field: keyof IsolatePayload) =>
    form.findingsByField.get(field) ?? [];

  /** E-14: Enter moves to the next field rather than submitting the form. */
  const onEnterAdvance = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter") return;
    const target = event.target as HTMLElement;
    if (target.tagName === "TEXTAREA" || target.getAttribute("role") === "combobox") return;
    if (target.tagName === "INPUT" || target.tagName === "SELECT") {
      event.preventDefault();
      const focusable = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
        ),
      );
      const index = focusable.indexOf(target);
      focusable[index + 1]?.focus();
    }
  };

  return (
    <form
      className="space-y-4 pb-28"
      onKeyDown={onEnterAdvance}
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave("new");
      }}
    >
      {inboundRecord && (
        <div className="rounded-md bg-brand-50 p-3 text-sm text-brand-900">
          <p>
            Pre-filled from the laboratory system, reference{" "}
            <span className="font-mono">{inboundRecord.external_id}</span>. Check
            every field against the register before saving.
          </p>
          {inboundRecord.unresolved.length > 0 && (
            <p className="mt-1">
              You still need to choose:{" "}
              <strong>
                {inboundRecord.unresolved.join(", ").replace(/_/g, " ")}
              </strong>
              .
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-900">
          {editId
            ? `Editing ${isDraft ? "draft" : "isolate"}${editingRecordId ? `: ${editingRecordId}` : ""}`
            : strings.entry.title}
        </h1>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowShortcuts(true)}
          aria-label={strings.entry.shortcuts}
        >
          ?
        </Button>
      </div>

      {/* ---- 1. Specimen ---- */}
      <Card>
        <CardHeader title={strings.entry.specimenCard} />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Field
            id="specimen_date"
            label={strings.entry.specimenDate}
            hint="dd/mm/yyyy or select from calendar"
            required
            findings={fieldFindings("specimen_date")}
          >
            <DatePicker
              id="specimen_date"
              placeholder="14/03/2026"
              value={dateText}
              invalid={Boolean(dateError)}
              onChange={(text, parsed) => {
                setDateText(text);
                if (!text.trim()) {
                  setDateError(null);
                  form.setField("specimen_date", null);
                  return;
                }
                // E-02: reject an impossible date rather than rolling it over.
                setDateError(parsed ? null : "That date does not exist.");
                form.setField("specimen_date", parsed);
              }}
            />
            {dateError && <p className="text-xs text-blocking">{dateError}</p>}
          </Field>

          <Field id="lab_number" label={strings.entry.labNumber} findings={fieldFindings("lab_number")}>
            <Input
              id="lab_number"
              value={form.values.lab_number ?? ""}
              onChange={(event) => form.setField("lab_number", event.target.value || null)}
            />
          </Field>

          <Field
            id="hospital_number"
            label={`${strings.entry.hospitalNumber} (${strings.common.optional})`}
            hint="Leading zeros are kept."
            findings={fieldFindings("hospital_number")}
          >
            <Input
              id="hospital_number"
              // Text, never a number input: 004321 must not become 4321 (E-03).
              inputMode="numeric"
              value={form.values.hospital_number ?? ""}
              onChange={(event) =>
                form.setField("hospital_number", event.target.value || null)
              }
            />
          </Field>

          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field id="age_value" label={strings.entry.age} findings={fieldFindings("age_value")}>
              <Input
                id="age_value"
                type="number"
                min={0}
                max={200}
                value={form.values.age_value ?? ""}
                onChange={(event) =>
                  form.setField(
                    "age_value",
                    event.target.value === "" ? null : Number(event.target.value),
                  )
                }
              />
            </Field>
            <Field id="age_unit" label={strings.entry.ageUnit}>
              <Select
                id="age_unit"
                options={AGE_UNIT_OPTIONS}
                value={form.values.age_unit ?? "years"}
                onChange={(event) =>
                  form.setField("age_unit", event.target.value as IsolatePayload["age_unit"])
                }
              />
            </Field>
          </div>

          <Field id="sex" label={strings.entry.sex}>
            <Select
              id="sex"
              options={SEX_OPTIONS}
              placeholder="Not recorded"
              value={form.values.sex ?? ""}
              onChange={(event) =>
                form.setField("sex", (event.target.value || null) as IsolatePayload["sex"])
              }
            />
          </Field>

          <Field id="ward" label={strings.entry.ward} findings={fieldFindings("ward_raw")}>
            <Combobox
              id="ward"
              value={form.values.ward_code}
              options={wardOptions}
              onChange={(code) => {
                form.setField("ward_code", code);
                const ward = wards.find((w) => w.code === code);
                form.setField("ward_raw", ward?.name ?? null);
              }}
            />
          </Field>

          <Field id="specimen_type" label={strings.entry.specimenType} required>
            <Combobox
              id="specimen_type"
              value={form.values.specimen_type}
              options={specimenOptions}
              onChange={(code) => form.setField("specimen_type", code)}
            />
          </Field>
        </CardBody>
      </Card>

      {/* ---- 2. Culture ---- */}
      <Card>
        <CardHeader title={strings.entry.cultureCard} />
        <CardBody>
          <Field
            id="culture_result"
            label={strings.entry.cultureResult}
            required
            findings={fieldFindings("culture_result")}
          >
            <Select
              id="culture_result"
              options={CULTURE_OPTIONS}
              placeholder="Select…"
              value={form.values.culture_result ?? ""}
              onChange={(event) =>
                form.setField(
                  "culture_result",
                  (event.target.value || null) as IsolatePayload["culture_result"],
                )
              }
            />
          </Field>

          {!form.showOrganismSection && form.values.culture_result && (
            <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
              {strings.entry.noGrowthNotice}
            </p>
          )}
        </CardBody>
      </Card>

      {/* ---- 3. Organism and AST ---- */}
      {form.showOrganismSection && (
        <Card>
          <CardHeader title={strings.entry.organismCard} />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="organism"
                label={strings.entry.organism}
                findings={fieldFindings("organism_code")}
              >
                <Combobox
                  id="organism"
                  value={form.values.organism_code}
                  options={organismOptions}
                  onChange={(code) => form.setField("organism_code", code)}
                />
              </Field>

              <Field id="isolate_number" label={strings.entry.isolateNumber}>
                <Select
                  id="isolate_number"
                  options={ISOLATE_NUMBER_OPTIONS}
                  value={form.values.isolate_number ?? "1st"}
                  onChange={(event) =>
                    form.setField(
                      "isolate_number",
                      event.target.value as IsolatePayload["isolate_number"],
                    )
                  }
                />
              </Field>
            </div>

            {form.values.organism_code === "other" && (
              <Field
                id="organism_as_written"
                label={strings.entry.organismOther}
                required
                findings={fieldFindings("organism_as_written")}
              >
                <Input
                  id="organism_as_written"
                  value={form.values.organism_as_written ?? ""}
                  onChange={(event) =>
                    form.setField("organism_as_written", event.target.value || null)
                  }
                />
              </Field>
            )}

            <div>
              <h3 className="mb-1 text-sm font-medium text-slate-700">
                {strings.entry.antibiotics}
              </h3>
              <p className="mb-2 text-xs text-slate-500">
                Leave a drug blank if it was not tested.
              </p>
              <FindingList findings={fieldFindings("ast_results")} className="mb-2" />
              <AntibioticGrid
                antibiotics={antibiotics}
                values={form.values.ast_results}
                onChange={form.setAst}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {/* ---- notes ---- */}
      <Card>
        <CardHeader title={strings.entry.notes} />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Field id="register_book" label={strings.entry.registerBook}>
            <Input
              id="register_book"
              value={form.values.register_book ?? ""}
              onChange={(event) => form.setField("register_book", event.target.value || null)}
            />
          </Field>
          <Field id="register_page" label={strings.entry.registerPage}>
            <Input
              id="register_page"
              value={form.values.register_page ?? ""}
              onChange={(event) => form.setField("register_page", event.target.value || null)}
            />
          </Field>
          <Field id="notes" label={strings.entry.notes} className="sm:col-span-3">
            <Textarea
              id="notes"
              value={form.values.notes ?? ""}
              onChange={(event) => form.setField("notes", event.target.value || null)}
            />
          </Field>
        </CardBody>
      </Card>

      {/* ---- sticky footer ---- */}
      <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
          {form.blockingFindings.length > 0 && (
            <p className="w-full text-xs text-blocking sm:w-auto sm:flex-1">
              {form.blockingFindings.length} item
              {form.blockingFindings.length === 1 ? "" : "s"} must be fixed before saving.
            </p>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (editId) {
                  navigate(-1);
                } else {
                  form.reset("new");
                }
              }}
            >
              {strings.common.cancel}
            </Button>
            <Button
              type="button"
              variant="secondary"
              loading={form.saving}
              onClick={() => void handleSave("draft")}
            >
              {strings.entry.saveDraft}
            </Button>
            {!editId && (
              <Button
                type="button"
                variant="secondary"
                loading={form.saving}
                onClick={() => void handleSave("next-organism")}
              >
                {strings.entry.saveAndNextOrganism}
              </Button>
            )}
            <Button type="submit" loading={form.saving}>
              {editId ? (isDraft ? "Submit isolate" : strings.common.save) : strings.entry.saveAndNew}
            </Button>
          </div>
        </div>
      </div>

      <ShortcutsOverlay open={showShortcuts} onOpenChange={setShowShortcuts} />
    </form>
  );
}
